import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { ProductDialog } from "@/components/admin/ProductDialog";

const numCls = "w-28 h-10 px-3 rounded-lg border border-slate-200 font-mono text-sm";

const SettingsCard = () => {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/settings").then((r) => setS((prev) => prev || r.data));
  }, []);
  const save = async () => {
    try {
      await api.put("/admin/settings", { max_items: Number(s.max_items), default_points: Number(s.default_points) });
      toast.success("Settings saved");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!s) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 flex flex-wrap items-end gap-6" data-testid="admin-settings-card">
      <label className="space-y-2 block"><span className="text-xs font-mono uppercase tracking-widest text-slate-500 block">Max items per kit</span>
        <input type="number" min="1" max="10" value={s.max_items} onChange={(e) => setS({ ...s, max_items: e.target.value })} className={numCls} data-testid="admin-max-items-input" />
      </label>
      <label className="space-y-2 block"><span className="text-xs font-mono uppercase tracking-widest text-slate-500 block">Default points for new emails</span>
        <input type="number" min="0" value={s.default_points} onChange={(e) => setS({ ...s, default_points: e.target.value })} className={numCls} data-testid="admin-default-points-input" />
      </label>
      <button onClick={save} className="h-10 px-5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-[#1A73E8]" data-testid="admin-save-settings-btn">Save settings</button>
    </div>
  );
};

const ProductRow = ({ p, onChange, onEdit }) => {
  const [pts, setPts] = useState(p.points);
  const patch = async (body) => {
    try {
      await api.patch(`/admin/products/${p.id}`, body);
      toast.success("Product updated");
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const del = async () => {
    if (!window.confirm(`Delete ${p.name}? Existing orders keep their items.`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Product deleted");
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="flex flex-wrap sm:flex-nowrap items-center gap-4 py-4 border-t border-slate-100 first:border-0" data-testid={`admin-product-row-${p.id}`}>
      <img src={p.image} alt="" className="w-14 h-14 rounded-xl object-cover" />
      <div className="flex-1 min-w-[160px]">
        <p className="font-semibold">{p.name}</p>
        <p className="text-xs text-slate-600 truncate" data-testid={`admin-product-description-${p.id}`}>{p.description || "No description"}</p>
        <p className="text-xs text-slate-400 truncate">{p.colors.join(", ")}{p.sizes.length ? ` · ${p.sizes.join("/")}` : ""}</p>
      </div>
      <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
      <input type="number" min="0" value={pts} onChange={(e) => setPts(e.target.value)} onBlur={() => Number(pts) !== p.points && patch({ points: Number(pts) })}
        className={numCls} data-testid={`admin-product-points-${p.id}`} />
      <Switch checked={p.active} onCheckedChange={(v) => patch({ active: v })} data-testid={`admin-product-active-${p.id}`} />
      <button onClick={() => onEdit(p)} className="p-2 rounded-full text-slate-500 hover:bg-slate-100" data-testid={`admin-product-edit-${p.id}`} aria-label="Edit"><Pencil className="w-4 h-4" /></button>
      <button onClick={del} className="p-2 rounded-full text-slate-400 hover:text-[#EA4335] hover:bg-red-50" data-testid={`admin-product-delete-${p.id}`} aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

export const CatalogTab = () => {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(undefined);
  const load = () => api.get("/admin/products").then((r) => setProducts(r.data));
  useEffect(() => {
    load();
  }, []);
  return (
    <div className="space-y-6">
      <SettingsCard />
      <div className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-mono uppercase tracking-widest text-slate-400">Products · points · visible</p>
          <button onClick={() => setEditing(null)} className="h-9 px-4 rounded-full bg-slate-900 text-white text-sm font-semibold flex items-center gap-1.5 whitespace-nowrap hover:bg-[#1A73E8]" data-testid="admin-add-product-btn">
            <Plus className="w-4 h-4" /> Add product
          </button>
        </div>
        {products.map((p) => <ProductRow key={`${p.id}-${p.points}`} p={p} onChange={load} onEdit={setEditing} />)}
      </div>
      <ProductDialog open={editing !== undefined} product={editing} onClose={() => setEditing(undefined)} onSaved={load} />
    </div>
  );
};
